import { useState, useEffect } from "react";
import axios from "axios";
import { Loader } from "components/lib";
import styles from "./styles.module.scss";
import {
  ExternalLink,
  FileText,
  Clipboard,
  Users,
  User,
  Folder,
  Mail,
  Settings,
  HelpCircle,
  Info,
} from "feather-icons-react";

export function ContactsManager() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [newContact, setNewContact] = useState({ name: "", email: "", icon: "mail", order: 1 });
  const [error, setError] = useState("");
  const [isIconDropdownOpen, setIsIconDropdownOpen] = useState(false);

  // Icons available to select from
  const iconMap = {
    "external-link": ExternalLink,
    "file-text": FileText,
    clipboard: Clipboard,
    user: User,
    users: Users,
    folder: Folder,
    mail: Mail,
    settings: Settings,
    "help-circle": HelpCircle,
    info: Info,
  };

  const availableIcons = Object.keys(iconMap);

  const renderIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || iconMap["mail"];
    return <IconComponent size={18} />;
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.iconSelector}`)) {
        setIsIconDropdownOpen(false);
      }
    };

    if (isIconDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isIconDropdownOpen]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/admin-contacts");
      const sortedContacts = (response.data.data.data || []).sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      setContacts(sortedContacts);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (contact) => {
    try {
      setError("");
      if (!contact.name || !contact.email) {
        setError("Name and Email are required");
        return;
      }

      if (editing) {
        // Update existing contact
        await axios.put(`/api/admin-contacts/${editing.id}`, contact);
        setEditing(null);
      } else {
        // Create new contact
        await axios.post("/api/admin-contacts", contact);
      }
      
      // Refetch all contacts to get updated positions (including any swapped items)
      await fetchContacts();
      setNewContact({ name: "", email: "", icon: "mail", order: contacts.length + 1 });
    } catch (error) {
      console.error("Error saving contact:", error);
      setError("Failed to save contact. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/admin-contacts/${id}`);
      setContacts(contacts.filter((contact) => contact.id !== id));
    } catch (error) {
      console.error("Error deleting contact:", error);
    }
  };

  const handleEdit = (contact) => {
    setEditing(contact);
    setNewContact({ ...contact });
  };

  const handleCancel = () => {
    setEditing(null);
    setNewContact({ name: "", email: "", icon: "mail", order: contacts.length + 1 });
    setError("");
  };

  if (loading) return <Loader />;

  return (
    <div className={styles.contactsManager}>
      <div className={styles.contactForm}>
        <h3>{editing ? "Edit Contact" : "Add New Contact"}</h3>
        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.formGroup}>
          <label>Name</label>
          <input
            type="text"
            value={newContact.name}
            onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
            placeholder="Enter contact name"
          />
        </div>

        <div className={styles.formGroup}>
          <label>Email</label>
          <input
            type="email"
            value={newContact.email}
            onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
            placeholder="Enter email address"
          />
        </div>

        <div className={styles.formGroup}>
          <label>Icon</label>
          <div className={styles.iconSelector}>
            <div
              className={styles.selectedIcon}
              onClick={() => setIsIconDropdownOpen(!isIconDropdownOpen)}
            >
              {renderIcon(newContact.icon)}
              <span>{newContact.icon}</span>
              <span className={styles.dropdownArrow}>▼</span>
            </div>
            {isIconDropdownOpen && (
              <div className={styles.iconDropdownMenu}>
                {availableIcons.map((icon) => (
                  <div
                    key={icon}
                    className={`${styles.iconOption} ${
                      newContact.icon === icon ? styles.selected : ""
                    }`}
                    onClick={() => {
                      setNewContact({ ...newContact, icon });
                      setIsIconDropdownOpen(false);
                    }}
                  >
                    {renderIcon(icon)}
                    <span>{icon}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>Position</label>
          <input
            type="number"
            min="1"
            value={newContact.order || 1}
            onChange={(e) => setNewContact({ ...newContact, order: parseInt(e.target.value) || 1 })}
            placeholder="Position (1 = top)"
          />
        </div>

        <div className={styles.formActions}>
          <button className={styles.saveButton} onClick={() => handleSave(newContact)}>
            {editing ? "Update Contact" : "Add Contact"}
          </button>

          {editing && (
            <button className={styles.cancelButton} onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className={styles.contactsList}>
        <h3>Current Contacts</h3>
        {contacts.length === 0 ? (
          <p>No contacts have been added yet.</p>
        ) : (
          <table className={styles.contactsTable}>
            <thead>
              <tr>
                <th>Position</th>
                <th>Name</th>
                <th>Email</th>
                <th>Icon</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id}>
                  <td>{contact.order || "1"}</td>
                  <td>{contact.name}</td>
                  <td>
                    <a href={`mailto:${contact.email}`} target="_blank" rel="noopener noreferrer">
                      {contact.email}
                    </a>
                  </td>
                  <td>
                    <div className={styles.iconCell}>
                      {renderIcon(contact.icon)}
                      <span>{contact.icon}</span>
                    </div>
                  </td>
                  <td className={styles.actions}>
                    <button className={styles.editButton} onClick={() => handleEdit(contact)}>
                      Edit
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDelete(contact.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
